import type { TrackProps } from './TrackContainer'
import TrackContainer from './TrackContainer'
import useTracksStore, {
  trackSelector,
} from '../../../shared/store/tracks.store.tsx'
import Icon from '../../common/Icon.tsx'
import { SidebarType } from '../../Sidebar/Sidebar.tsx'
import useUiStore from '../../../shared/store/ui.store.tsx'
import { memo, useEffect, useRef, forwardRef } from 'react'
import VoiceClip from '@/components/Editor/Tracks/Clips/VoiceClip.tsx'
import SpeechClip from '@/components/Editor/Tracks/Clips/SpeechClip.tsx'
import { isVoiceClip } from '@/features/clips/domain/models/clip.ts'

const VoiceTrack = forwardRef<HTMLDivElement, TrackProps>(
  ({ trackId }, scrollRef) => {
    const track = useTracksStore(trackSelector(trackId))
    const setSidebarType = useUiStore((state) => state.setSidebarType)
    const pixelsPerSecond = useUiStore((state) => state.pixelsPerSecond)

    const previousLength = useRef(track.clips.length)

    useEffect(() => {
      if (track.clips.length > previousLength.current) {
        if (scrollRef && typeof scrollRef !== 'function' && scrollRef.current) {
          const scrollable = scrollRef.current
          const lastClip = track.clips[track.clips.length - 1]
          if (!lastClip?.startAt) return
          const scrollLeft =
            (lastClip.startAt + lastClip.startOffset) * pixelsPerSecond - 300

          scrollable.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          })
        }
      }

      previousLength.current = track.clips.length
    }, [track.clips, pixelsPerSecond, scrollRef])

    return (
      <div className="flex">
        <div className="h-[134px] min-w-4 border-b border-b-gray-800" />
        <TrackContainer
          contextMenuVM={[
            {
              key: 'ls_track_context_menu_add_script',
              action: () => setSidebarType(SidebarType.Scripts),
              icon: <Icon name="transcript" fill="white" />,
            },
          ]}
        >
          <div className="relative h-full w-full">
            {track.clips.map((clip) => (
              <div key={clip.id}>
                {isVoiceClip(clip) ? (
                  <VoiceClip clip={clip} />
                ) : (
                  <SpeechClip clip={clip} />
                )}
              </div>
            ))}
          </div>
        </TrackContainer>
        <div className="h-[134px] w-4 border-b border-b-gray-800" />
      </div>
    )
  }
)

VoiceTrack.displayName = 'VoiceTrack'
export default memo(VoiceTrack)
